import { ChangeDetectionStrategy, Component, computed, Input, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer } from '../cell-renderer';
import { MultiLineAttribute, MultiLineCellConfig, MultiLineLine } from './multi-line-cell-renderer.model';
import { MatIconModule } from '@angular/material/icon';

export interface MultiLineCellRendererConfig {
  multiLineCellConfig: MultiLineCellConfig;
  [key: string]: any;
}

interface ResolvedAttribute {
  label?: string;
  value: string;
  clickable: boolean;
  onClick?: (rowData: Record<string, any>) => void;
  tooltip?: string | null;
}

interface ResolvedLine {
  attributes: ResolvedAttribute[];
  separator: string;
}

@Component({
  selector: 'cbre-multi-line-cell-renderer',
  template: `
    <div class="cbre-multi-line-cell" [matTooltip]="cellTooltip()" matTooltipPosition="above">
      <div class="cbre-multi-line-cell__lines">
        @for (line of resolvedLines(); track $index; let first = $first) {
          <div class="cbre-multi-line-cell__line">
            @for (attr of line.attributes; track $index; let last = $last) {
              <span class="cbre-multi-line-cell__attr">
                <span
                  class="cbre-multi-line-cell__content"
                  [matTooltip]="attr.clickable ? attr?.tooltip : null"
                  [matTooltipPosition]="'above'"
                  [class.cbre-multi-line-cell__attr--clickable]="attr.clickable"
                  (click)="attr.clickable && attr.onClick ? attr.onClick(rowData()) : null"
                >
                  @if (attr.label) {
                    <span class="cbre-multi-line-cell__label">{{ attr.label }}:&nbsp;</span>
                  }
                  <span
                    [matTooltip]="!cellTooltip() && !attr.clickable ? attr.tooltip : null"
                    [matTooltipPosition]="'above'"
                    class="cbre-multi-line-cell__value"
                    >{{ attr.value }}</span
                  >
                </span>
                @if (!last) {
                  <span class="cbre-multi-line-cell__separator">&nbsp;{{ line.separator }}&nbsp;</span>
                }
              </span>
            }
            @if (first && showIcon() && line.attributes.length === 1) {
              <span
                class="cbre-multi-line-cell__info-icon"
                [matTooltip]="resolvedCellIconTooltip()"
                matTooltipPosition="above"
                (click)="iconEventFn() ? iconEventFn()!(rowData()) : null"
              >
                <mat-icon [svgIcon]="cellIcon"></mat-icon>
              </span>
            }
          </div>
        } @empty {
          @if (noDataLabel) {
            <span
              [class.cbre-multi-line-cell__attr--clickable]="noDataLabelClickable()"
              (click)="noDataLabel && noDataLabelClick ? noDataLabelClick(rowData()) : null"
              class="cbre-multi-line-cell__no-data"
              >{{ noDataLabel ?? '-' }}</span
            >
          }
        }
      </div>
      @if (showIcon() && resolvedLines().length && resolvedLines()[0]?.attributes?.length !== 1) {
        <div
          class="cbre-multi-line-cell__info-icon"
          [matTooltip]="resolvedCellIconTooltip()"
          matTooltipPosition="above"
          (click)="iconEventFn() ? iconEventFn()!(rowData()) : null"
        >
          <mat-icon [svgIcon]="cellIcon"></mat-icon>
        </div>
      }
    </div>
  `,
  styleUrls: ['./multi-line-cell-renderer.component.scss'],
  imports: [MatTooltipModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiLineCellRendererComponent implements CellRenderer<MultiLineCellRendererConfig> {
  private _data = signal<Record<string, any>>({});
  private _config = signal<MultiLineCellRendererConfig | null>(null);
  cellIcon: string | null = null;
  noDataLabel: string | null = null;
  noDatalabelClickable = signal(false);
  noDataLabelClick: ((rowData: Record<string, any>) => void) | null = null;

  @Input() set data(data: Record<string, any>) {
    this._data.set(data ?? {});
  }

  @Input() set config(config: MultiLineCellRendererConfig) {
    this._config.set(config);
    this.cellIcon =
      (config.multiLineCellConfig.cellIcon && (config.multiLineCellConfig.cellIcon(this._data()) as string)) || null;

    this.noDataLabel = config.multiLineCellConfig?.noDataLabel ?? null;
    this.noDataLabelClick = config.multiLineCellConfig?.noDataLabelClick ?? null;
  }

  /** Expose raw row data for template click handler */
  readonly rowData = computed(() => this._data());

  readonly resolvedLines = computed<ResolvedLine[]>(() => {
    const data = this._data();
    const config = this._config();
    const lines: MultiLineLine[] = config?.multiLineCellConfig?.lines ?? [];

    return [...lines]
      .sort((a, b) => a.position - b.position)
      .map(line => ({
        separator: line.separator ?? '',
        attributes: [...line.attributes]
          .sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return 0; // Keep original order if 'order' is missing
          })
          .map(attr => this.resolveAttribute(attr, data))
          .filter(attr => attr.value !== '' && attr.value != null),
      }))
      .filter(line => line.attributes.length > 0);
  });

  readonly resolvedCellIconTooltip = computed<string | null>(() => {
    const cfg = this._config()?.multiLineCellConfig;
    const tooltip = cfg?.cellIconTooltip ?? null;
    if (!tooltip) return null;
    return cfg?.translateFn ? cfg.translateFn(tooltip) : tooltip;
  });

  readonly showIcon = computed<boolean>(() => {
    const cfg = this._config()?.multiLineCellConfig;
    if (!cfg?.cellIcon) return false;
    return cfg.showIconFn ? cfg.showIconFn(this._data()) : true;
  });

  readonly noDataLabelClickable = computed(() => {
    const cfg = this._config()?.multiLineCellConfig;
    return !!cfg?.noDataLabel;
  });

  readonly iconEventFn = computed(() => this._config()?.multiLineCellConfig?.iconEvent ?? null);

  readonly cellTooltip = computed<string | null>(() => {
    const config = this._config();
    if (!config?.multiLineCellConfig?.showCellTooltip) return null;

    return (
      this.resolvedLines()
        .map(line =>
          line.attributes
            .map(attr => (attr.label ? `${attr.label}: ${attr.value}` : attr.value))
            .join(` ${line.separator} `)
        )
        .join('\n') || null
    );
  });

  private resolveAttribute(attr: MultiLineAttribute, data: Record<string, any>): ResolvedAttribute {
    let value: string;

    if (attr.displayFn != null) {
      value = attr.displayFn(data);
    } else {
      const key = attr.path ?? attr.field;
      value = key != null ? data[key] : '';
    }

    const translateFn = this._config()?.multiLineCellConfig?.translateFn;
    const rawTooltip = attr.tooltip && (attr.tooltip(data) as string);
    const tooltip = rawTooltip && translateFn ? translateFn(rawTooltip) : rawTooltip;

    return {
      label: attr.label,
      value: value != null ? String(value) : '',
      clickable: !!attr.clickable,
      onClick: attr.onClick,
      tooltip,
    };
  }

  render(): void {
    // required by CellRenderer interface
  }
}
